import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { HERO_SLIDES } from "../../constants/siteData";

export const HeroSlider = () => {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, duration: 40 });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    onSelect();
    const t = setInterval(() => embla.scrollNext(), 6500);
    return () => {
      clearInterval(t);
      embla.off("select", onSelect);
    };
  }, [embla, onSelect]);

  return (
    <section
      id="home"
      className="relative bg-navy overflow-hidden"
      data-testid="hero-section"
    >
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex">
          {HERO_SLIDES.map((slide, i) => (
            <div key={i} className="flex-[0_0_100%] min-w-0 relative">
              <div className="hero-slide">
                <img src={slide.image} alt="" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content overlay */}
      <div className="absolute inset-0 z-10 flex items-end pb-24 md:pb-32 pointer-events-none">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-3xl pointer-events-auto"
            >
              <p className="kicker blink mb-6" data-testid="hero-kicker">
                {HERO_SLIDES[selectedIndex].kicker}
              </p>
              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter text-cream mb-6">
                {HERO_SLIDES[selectedIndex].title}
              </h1>
              <p className="text-base md:text-lg text-white/70 max-w-2xl mb-10 leading-relaxed">
                {HERO_SLIDES[selectedIndex].subtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#categories" className="btn-coral" data-testid="hero-primary-cta">
                  Explore Portfolio <ArrowRight size={18} />
                </a>
                <a href="#contact" className="btn-ghost" data-testid="hero-secondary-cta">
                  Partner with us <ArrowUpRight size={16} />
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Slide counter + arrows (right) */}
      <div className="absolute right-6 md:right-10 bottom-24 md:bottom-32 z-20 flex flex-col items-end gap-4 pointer-events-auto">
        <div className="font-display text-3xl text-cream tracking-tight" data-testid="hero-slide-counter">
          <span className="text-coral">{String(selectedIndex + 1).padStart(2, "0")}</span>
          <span className="text-white/30 mx-2">/</span>
          <span className="text-white/50 text-xl">{String(HERO_SLIDES.length).padStart(2, "0")}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => embla && embla.scrollPrev()}
            className="w-11 h-11 rounded-full border border-white/20 text-cream hover:bg-coral hover:border-coral hover:text-navy flex items-center justify-center"
            style={{ transitionProperty: "background-color, border-color, color", transitionDuration: "200ms" }}
            data-testid="hero-prev"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => embla && embla.scrollNext()}
            className="w-11 h-11 rounded-full border border-white/20 text-cream hover:bg-coral hover:border-coral hover:text-navy flex items-center justify-center"
            style={{ transitionProperty: "background-color, border-color, color", transitionDuration: "200ms" }}
            data-testid="hero-next"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Vertical text ribbon */}
      <div className="hidden md:block absolute left-6 top-1/2 -translate-y-1/2 z-20 vertical-text">
        Scroll — Explore Avado
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 h-[3px] bg-white/5">
        <motion.div
          key={selectedIndex}
          className="h-full bg-coral"
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 6.5, ease: "linear" }}
        />
      </div>
    </section>
  );
};
